import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * One live socket per conversation id, owned entirely by this hook.
 *
 * Why this exists: the rep chat used to open its socket inside the same
 * effect that fetched the transcript, "only if no socket exists yet", and
 * keep it in a ref shared across session switches. A fast A → B switch let
 * A's slow fetch finish last, open a socket to A and store it, and B's run
 * then found "a socket" and skipped. The screen showed B while every send
 * went to A (2026-09-21, a rep's replies landed in another visitor's chat).
 *
 * Guarantees:
 * - the socket is keyed on the conversation id; changing the id closes the
 *   old socket synchronously and every continuation of the old run (connect,
 *   reconnect timer, open/close handlers) is invalidated by a generation
 *   counter before it can touch shared state;
 * - `send` refuses unless the socket is OPEN and serves the id the caller
 *   passes, so a caller can never write to a conversation it is not showing;
 * - reconnects back off exponentially and stop on close codes that mean
 *   "do not come back" (policy/auth 1008, application 4xxx).
 */

export type SocketFrame = Record<string, any>;

export interface ConversationSocketHandlers {
    onFrame: (frame: SocketFrame) => void;
    /** Fires on every successful open, including reconnects. */
    onOpen?: (info: { reconnect: boolean }) => void;
    /** Fires on every close of the current socket, before any reconnect. */
    onClose?: (event: CloseEvent) => void;
}

export interface ConversationSocket {
    /** True while the socket for the current conversation id is OPEN. */
    isOpen: boolean;
    /**
     * Send a frame for `conversationId`. Returns false (and sends nothing)
     * when the socket is not open or serves a different conversation.
     */
    send: (conversationId: string, frame: SocketFrame) => boolean;
}

const NO_RECONNECT_CLOSE_CODES = new Set([1008]);
const MAX_BACKOFF_MS = 30000;

export function useConversationSocket(
    conversationId: string | null,
    connect: (url: string) => Promise<WebSocket | undefined>,
    handlers: ConversationSocketHandlers,
): ConversationSocket {
    const [isOpen, setIsOpen] = useState(false);
    const socketRef = useRef<WebSocket | null>(null);
    const socketIdRef = useRef<string | null>(null);
    const generationRef = useRef(0);
    const handlersRef = useRef(handlers);
    const connectRef = useRef(connect);
    handlersRef.current = handlers;
    connectRef.current = connect;

    useEffect(() => {
        const generation = ++generationRef.current;
        const isStale = () => generation !== generationRef.current;
        let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
        let attempts = 0;
        let hasOpenedOnce = false;

        const clearReconnect = () => {
            if (reconnectTimer) {
                clearTimeout(reconnectTimer);
                reconnectTimer = null;
            }
        };

        const scheduleReconnect = () => {
            if (isStale() || reconnectTimer) return;
            const delay = Math.min(1000 * 2 ** attempts, MAX_BACKOFF_MS);
            attempts += 1;
            reconnectTimer = setTimeout(() => {
                reconnectTimer = null;
                void open();
            }, delay);
        };

        const open = async () => {
            if (!conversationId || isStale()) return;
            let ws: WebSocket | undefined;
            try {
                ws = await connectRef.current(`/ws/${conversationId}?role=assistant`);
            } catch (error) {
                console.error('Failed to establish conversation socket:', error);
            }
            if (isStale()) {
                ws?.close();
                return;
            }
            if (!ws) {
                // No token yet (auth refresh in flight) or a thrown connect:
                // keep trying with backoff instead of leaving a dead chat.
                scheduleReconnect();
                return;
            }
            socketRef.current = ws;
            socketIdRef.current = conversationId;

            ws.onopen = () => {
                if (isStale()) return;
                attempts = 0;
                clearReconnect();
                setIsOpen(true);
                const reconnect = hasOpenedOnce;
                hasOpenedOnce = true;
                handlersRef.current.onOpen?.({ reconnect });
            };

            ws.onmessage = (event) => {
                if (isStale()) return;
                let frame: SocketFrame;
                try {
                    frame = JSON.parse(event.data);
                } catch (error) {
                    console.error('Malformed conversation socket frame:', error);
                    return;
                }
                handlersRef.current.onFrame(frame);
            };

            ws.onclose = (event) => {
                if (isStale()) return;
                if (socketRef.current === ws) {
                    socketRef.current = null;
                    socketIdRef.current = null;
                }
                setIsOpen(false);
                handlersRef.current.onClose?.(event);
                const terminal = NO_RECONNECT_CLOSE_CODES.has(event.code) || (event.code >= 4000 && event.code < 5000);
                if (!terminal) scheduleReconnect();
            };

            ws.onerror = (error) => {
                console.error('Conversation socket error:', error);
            };
        };

        setIsOpen(false);
        void open();

        return () => {
            // Invalidate this run before anything else so no handler or
            // continuation of it can act for the next conversation id.
            generationRef.current += 1;
            clearReconnect();
            const ws = socketRef.current;
            socketRef.current = null;
            socketIdRef.current = null;
            if (ws) {
                ws.onopen = null;
                ws.onmessage = null;
                ws.onclose = null;
                ws.onerror = null;
                ws.close();
            }
            setIsOpen(false);
        };
    }, [conversationId]);

    const send = useCallback((forConversationId: string, frame: SocketFrame): boolean => {
        const ws = socketRef.current;
        if (!ws || ws.readyState !== WebSocket.OPEN) return false;
        if (!forConversationId || socketIdRef.current !== forConversationId) return false;
        ws.send(JSON.stringify(frame));
        return true;
    }, []);

    return { isOpen, send };
}
